/**
 * Consent-Gated Script Loader
 *
 * Loads tracking scripts only after user consent.
 * Called when consent state changes.
 */

import { consentStore } from "@/stores/consent-store";

const GA4_ID = "G-Z4BHTCWQQ4";
const GTM_ID = "GTM-TNHK86B";
const TIKTOK_ID = "CA186F3C77U96QF487D0";

const loadedScripts = new Set<string>();

function loadScript(id: string, loader: () => void): void {
  if (loadedScripts.has(id)) return;
  loadedScripts.add(id);
  loader();
}

function loadGA4(): void {
  loadScript("ga4", () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: unknown[]): void {
      window.dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", GA4_ID);

    console.log("[Consent] GA4 loaded");
  });
}

function loadGTM(): void {
  loadScript("gtm", () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);

    console.log("[Consent] GTM loaded");
  });
}

function loadTripleWhale(): void {
  loadScript("triplewhale", () => {
    const script = document.createElement("script");
    script.textContent = `
      ~function(W,H,A,L,E,_,B,N){function O(U,T,H,R){void 0===R&&(R=!1),H=new XMLHttpRequest,H.open("GET",U,!0),H.send(null),H.onreadystatechange=function(){4===H.readyState&&200===H.status?(R=H.responseText,U.includes(".txt")?eval(R):N[B]=R):(299<H.status||H.status<200)&&T&&!R&&(R=!0,O(U,T-1))}}if(N=window,!N[H+"sn"]){N[H+"sn"]=1;try{A.setItem(H,1+(0|A.getItem(H)||0)),(E=JSON.parse(A.getItem(H+"U")||"[]")).push(location.href),A.setItem(H+"U",JSON.stringify(E))}catch(e){}A.getItem('"!nC\`')||(A=N,A[H]||(L=function(){return Date.now().toString(36)+"_"+Math.random().toString(36)},E=A[H]=function(t,e){return W=L(),(E._q=E._q||[]).push([W,t,e]),W},E.ch=W,B="configSecurityConfModel",N[B]=1,O("//conf.config-security.com/model",0),O("//triplewhale-pixel.web.app/triplefw.txt?",5)))}}("","TriplePixel",localStorage);
    `;
    document.head.appendChild(script);

    console.log("[Consent] TripleWhale loaded");
  });
}

function loadTikTok(): void {
  loadScript("tiktok", () => {
    const script = document.createElement("script");
    script.textContent = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
        var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
        n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
        ttq.load('${TIKTOK_ID}');
        ttq.page();
      }(window, document, 'ttq');
    `;
    document.head.appendChild(script);

    console.log("[Consent] TikTok loaded");
  });
}

export function loadConsentedScripts(): void {
  const state = consentStore.getState();

  if (state.analytics) {
    loadGA4();
    loadGTM();
    loadTripleWhale();
  }

  if (state.marketing) {
    loadTikTok();
  }
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
